import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'

export interface TelegramChat {
  id: string
  telegram_chat_id: string
  telegram_user_id: string | null
  username: string | null
  first_name: string | null
  last_name: string | null
  profile_name: string | null
  chat_type: string
  client_id: string | null
  assigned_agent_id: string | null
  assigned_at: string | null
  ai_assigned?: boolean
  ai_agent_name?: string | null
  status: string
  last_message_text: string | null
  last_message_at: string
  last_message_direction: string
  unread_count: number
  created_at: string
  updated_at: string
  client_name: string | null
  client_email: string | null
}

export interface TelegramMessageSender {
  id: number
  username?: string
  first_name?: string
  last_name?: string
}

export interface TelegramMessage {
  id: string
  chat_id: string
  telegram_message_id: string
  direction: 'inbound' | 'outbound'
  message_type: string
  body: string | null
  media_file_id: string | null
  media_mime_type: string | null
  media_filename: string | null
  status: string
  error_message: string | null
  created_at: string
  sent_at: string // Alias for created_at, used by grouping functions
  conversation_id: string | null
  // Sender info extracted from raw_payload (for group chats)
  sender_id?: string | null
  sender_name?: string | null
  sender_username?: string | null
}

export interface TelegramConversation {
  id: string
  reference_id: string
  status: string
  subject: string | null
  is_archived: boolean
}

export interface PendingTelegramMessage {
  localId: string
  body: string | null
  message_type: string
  status: 'sending' | 'failed'
  created_at: string
}

export interface TelegramAccount {
  connected: boolean
  botId?: string
  botUsername?: string
  displayName?: string
  reason?: string
}

export function chatDisplayName(chat: TelegramChat): string {
  // For groups/supergroups/channels, use profile_name (which stores the title)
  if (isGroupChat(chat) && chat.profile_name) return chat.profile_name
  if (chat.profile_name) return chat.profile_name
  if (chat.client_name) return chat.client_name
  if (chat.first_name) {
    return chat.last_name ? `${chat.first_name} ${chat.last_name}` : chat.first_name
  }
  if (chat.username) return `@${chat.username}`
  return `Telegram ${chat.telegram_chat_id}`
}

export function isGroupChat(chat: TelegramChat): boolean {
  return chat.chat_type === 'group' || chat.chat_type === 'supergroup'
}

export function isChannelChat(chat: TelegramChat): boolean {
  return chat.chat_type === 'channel'
}

export function getChatTypeLabel(chat: TelegramChat): string {
  switch (chat.chat_type) {
    case 'private': return 'Private Chat'
    case 'group': return 'Group'
    case 'supergroup': return 'Supergroup'
    case 'channel': return 'Channel'
    default: return 'Chat'
  }
}

export function useTelegramChats(search?: string) {
  const [chats, setChats] = useState<TelegramChat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchChats = useCallback(async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : ''
      const res = await api.get<{ chats: TelegramChat[] }>(`/agent/telegram-chats${params}`)
      setChats(res.chats ?? [])
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    void fetchChats()
    // Polling stays as a fallback; the bump makes new messages show up immediately.
    const interval = setInterval(() => void fetchChats(), 5000)
    const socket = getSocket()
    let timer: ReturnType<typeof setTimeout> | null = null
    const onBump = (payload: { channel?: string }) => {
      if (payload.channel !== 'telegram') return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => void fetchChats(), 400)
    }
    socket.on('chat:list:bump', onBump)
    return () => {
      clearInterval(interval)
      if (timer) clearTimeout(timer)
      socket.off('chat:list:bump', onBump)
    }
  }, [fetchChats])

  return { chats, loading, error, refetch: fetchChats }
}

export function useTelegramChatMessages(chatId: string | null) {
  const [chat, setChat] = useState<TelegramChat | null>(null)
  const [messages, setMessages] = useState<TelegramMessage[]>([])
  const [conversations, setConversations] = useState<TelegramConversation[]>([])
  const [pending, setPending] = useState<PendingTelegramMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const beforeRef = useRef<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchMessages = useCallback(async () => {
    if (!chatId) return
    try {
      const res = await api.get<{
        chat: TelegramChat
        messages: TelegramMessage[]
        conversations: TelegramConversation[]
        hasMore: boolean
        before?: string
      }>(`/agent/telegram-chats/${chatId}`)
      setChat(res.chat)
      setMessages(res.messages ?? [])
      setConversations(res.conversations ?? [])
      setHasMore(res.hasMore)
      beforeRef.current = res.before ?? null
    } catch {
      // Ignore errors during polling
    } finally {
      setLoading(false)
    }
  }, [chatId])

  const loadMore = useCallback(async () => {
    if (!chatId || !hasMore || loadingMore || !beforeRef.current) return
    setLoadingMore(true)
    try {
      const res = await api.get<{
        messages: TelegramMessage[]
        hasMore: boolean
        before?: string
      }>(`/agent/telegram-chats/${chatId}?before=${encodeURIComponent(beforeRef.current)}&limit=50`)
      setMessages((prev) => [...(res.messages ?? []), ...prev])
      setHasMore(res.hasMore)
      beforeRef.current = res.before ?? null
    } finally {
      setLoadingMore(false)
    }
  }, [chatId, hasMore, loadingMore])

  useEffect(() => {
    if (!chatId) {
      setChat(null)
      setMessages([])
      setConversations([])
      setPending([])
      setLoading(false)
      return
    }
    setLoading(true)
    beforeRef.current = null
    setPending([])
    void fetchMessages()
    pollRef.current = setInterval(() => void fetchMessages(), 3000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [chatId, fetchMessages])

  const sendOptimistic = useCallback(
    async (
      draft: Omit<PendingTelegramMessage, 'localId' | 'status' | 'created_at'>,
      send: () => Promise<unknown>,
    ) => {
      const localId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      setPending((prev) => [
        ...prev,
        { ...draft, localId, status: 'sending', created_at: new Date().toISOString() },
      ])
      try {
        await send()
        await fetchMessages()
        setPending((prev) => prev.filter((p) => p.localId !== localId))
      } catch (err) {
        setPending((prev) =>
          prev.map((p) => (p.localId === localId ? { ...p, status: 'failed' } : p)),
        )
        throw err
      }
    },
    [fetchMessages],
  )

  const dismissPending = useCallback((localId: string) => {
    setPending((prev) => prev.filter((p) => p.localId !== localId))
  }, [])

  const sendMessage = useCallback(
    async (text: string) => {
      if (!chatId || !text.trim()) return
      await sendOptimistic({ body: text.trim(), message_type: 'text' }, () =>
        api.post(`/agent/telegram-chats/${chatId}/reply`, { text }),
      )
    },
    [chatId, sendOptimistic],
  )

  const sendAttachment = useCallback(
    async (file: File, caption?: string) => {
      if (!chatId) return
      const formData = new FormData()
      formData.append('file', file)
      if (caption) formData.append('caption', caption)
      
      const messageType = file.type.startsWith('image/') ? 'image'
        : file.type.startsWith('video/') ? 'video'
        : file.type.startsWith('audio/') ? 'audio'
        : 'document'
      
      await sendOptimistic({ body: caption ?? `📎 ${file.name}`, message_type: messageType }, async () => {
        // FormData is handled automatically by the api client - no custom headers needed
        await api.post(`/agent/telegram-chats/${chatId}/send-attachment`, formData)
      })
    },
    [chatId, sendOptimistic],
  )

  const saveContact = useCallback(
    async (input: { fullName: string; email?: string }) => {
      if (!chatId) return null
      const res = await api.post<{ client: { id: string; full_name: string }; created: boolean }>(
        `/agent/telegram-chats/${chatId}/save-contact`,
        input,
      )
      await fetchMessages()
      return res
    },
    [chatId, fetchMessages],
  )

  const createTicket = useCallback(
    async (messageIds?: string[], subject?: string) => {
      if (!chatId) return null
      const res = await api.post<{ conversationId: string; referenceId: string }>(
        `/agent/telegram-chats/${chatId}/create-ticket`,
        {
          ...(messageIds && messageIds.length > 0 ? { messageIds } : {}),
          ...(subject ? { subject } : {}),
        },
      )
      await fetchMessages()
      return res
    },
    [chatId, fetchMessages],
  )

  const markRead = useCallback(async () => {
    if (!chatId) return
    await api.post(`/agent/telegram-chats/${chatId}/read`, {})
  }, [chatId])

  const assignToMe = useCallback(async () => {
    if (!chatId) return
    await api.post(`/agent/telegram-chats/${chatId}/assign`, {})
    await fetchMessages()
  }, [chatId, fetchMessages])

  const unassign = useCallback(async () => {
    if (!chatId) return
    await api.post(`/agent/telegram-chats/${chatId}/unassign`, {})
    await fetchMessages()
  }, [chatId, fetchMessages])

  return {
    chat,
    messages,
    conversations,
    pending,
    loading,
    loadingMore,
    hasMore,
    sendMessage,
    sendAttachment,
    saveContact,
    createTicket,
    markRead,
    assignToMe,
    unassign,
    dismissPending,
    loadMore,
    fetchMessages,
  }
}

export function useTelegramAccount() {
  const [account, setAccount] = useState<TelegramAccount | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void api
      .get<TelegramAccount>('/agent/telegram-chats/account')
      .then((res) => {
        if (!cancelled) setAccount(res)
      })
      .catch(() => {
        if (!cancelled) setAccount({ connected: false, reason: 'error' })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { account, loading }
}
